import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCloudSun,
  faDownload,
  faEarthEurope,
  faImage,
  faLayerGroup,
  faMap,
  faSatellite,
} from '@fortawesome/free-solid-svg-icons';
import './StationPage.css';

const productIcons = {
  map: faMap,
  color: faCloudSun,
  msa: faLayerGroup,
  analysis: faImage,
  world: faEarthEurope,
  channel: faSatellite,
};

function displayRotation() {
  return 0;
}

function stationAssetPath(src) {
  if (!src) return null;
  if (src === 'latest.png') return '/station/latest.png';
  if (src.startsWith('../decoded/')) return `/station/${src.replace('../decoded/', 'decoded/')}`;
  if (src.startsWith('/')) return src;
  return `/station/${src}`;
}

function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return 'recorded';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / (1024 ** index)).toFixed(index > 1 ? 1 : 0)} ${units[index]}`;
}

function formatDate(value) {
  if (!value) return 'pending';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function runShortTitle(run) {
  return (run?.runId || run?.title || 'station pass')
    .replace(/^(\d{8})T(\d{6})Z_?/, '$1 $2 ')
    .replace(/_/g, ' ');
}

function downloadFilename(run, product) {
  const runId = run?.runId || 'station-pass';
  const productId = product?.viewKey || product?.label || 'image';
  const extension = product?.src?.match(/\.(png|jpe?g|webp)$/i)?.[1] || 'png';
  return `${runId}_${productId}.${extension}`.replace(/[^a-z0-9._-]+/gi, '-');
}

function normalizeRuns(items) {
  return items
    .filter((item) => item.kind === 'Local run')
    .map((item) => {
      const fallbackProduct = {
        icon: 'IMG',
        label: 'Best',
        description: item.subtitle || 'Best decoded image from this pass.',
        src: item.src,
        mtime: item.mtime,
      };
      const allProducts = Array.isArray(item.products) && item.products.length
        ? item.products
        : (item.src ? [fallbackProduct] : []);
      const preferFilled = (matches) => matches.find((product) => product.src.includes('(Filled)')) || matches[0];
      const pick = (key, label, predicate) => {
        const product = preferFilled(allProducts.filter(predicate));
        return product ? { ...product, viewKey: key, label } : null;
      };
      const products = [
        pick('map', 'Corrected Map', (product) => product.label === 'Corrected Map'),
        pick('color', 'False Color', (product) => product.label === 'Corrected Color'),
        pick('msa', 'MSA Composite', (product) => product.label === 'Corrected MSA'),
        pick('analysis', '2-2-1 Analysis', (product) => product.label === 'Corrected 2-2-1'),
        pick('world', 'World Projection', (product) => product.src.includes('_projected.')),
        pick('channel', 'Raw Channel', (product) => product.label === 'Channel 3'),
      ].filter(Boolean);
      return { ...item, products };
    });
}

function StationPage() {
  const [runs, setRuns] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [selectedProductSrc, setSelectedProductSrc] = useState(null);
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 });
  const dragRef = useRef(null);

  useEffect(() => {
    async function loadStation() {
      try {
        const galleryRes = await fetch('/station/gallery.json', { cache: 'no-store' });
        if (galleryRes.ok) {
          const nextRuns = normalizeRuns(await galleryRes.json());
          setRuns(nextRuns);
          setSelectedRunId((current) => current || nextRuns[0]?.runId || nextRuns[0]?.title || null);
          setSelectedProductSrc((current) => current || nextRuns[0]?.products?.[0]?.src || nextRuns[0]?.src || null);
        }
      } catch (error) {
        console.warn('Unable to load station data', error);
      }
    }

    loadStation();
    const refresh = window.setInterval(loadStation, 60000);
    return () => window.clearInterval(refresh);
  }, []);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const selectedRun = useMemo(() => {
    return runs.find((run) => (run.runId || run.title) === selectedRunId) || runs[0] || null;
  }, [runs, selectedRunId]);

  const products = useMemo(() => selectedRun?.products || [], [selectedRun]);
  const selectedProduct = useMemo(() => {
    return products.find((product) => product.src === selectedProductSrc) || products[0] || null;
  }, [products, selectedProductSrc]);
  const selectedSrc = stationAssetPath(selectedProduct?.src || selectedRun?.src || null);
  const imageRotation = displayRotation(selectedRun, selectedProduct);

  useEffect(() => {
    setView({ scale: 1, x: 0, y: 0 });
  }, [selectedRun?.runId, selectedProduct?.src]);

  function selectRun(run) {
    setSelectedRunId(run.runId || run.title);
    setSelectedProductSrc(run.products?.[0]?.src || run.src || null);
  }

  function adjustZoom(delta) {
    setView((current) => ({
      ...current,
      scale: Math.min(6, Math.max(1, Number((current.scale + delta).toFixed(2)))),
    }));
  }

  function handleWheel(event) {
    event.preventDefault();
    adjustZoom(event.deltaY > 0 ? -0.18 : 0.18);
  }

  function startDrag(event) {
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: view.x,
      originY: view.y,
    };
  }

  function moveDrag(event) {
    if (!dragRef.current || dragRef.current.pointerId !== event.pointerId) return;
    setView((current) => ({
      ...current,
      x: dragRef.current.originX + event.clientX - dragRef.current.startX,
      y: dragRef.current.originY + event.clientY - dragRef.current.startY,
    }));
  }

  function endDrag() {
    dragRef.current = null;
  }

  return (
    <div className="station-page clean">
      <Helmet>
        <title>Station | DJFracking</title>
        <meta name="description" content="DJFracking weather satellite station pass and channel viewer." />
      </Helmet>
      <main className="station-console">
        <div className="station-corner-status" aria-label="Displayed satellite image">
          <a href="/" aria-label="Return to DJFracking home">
            <FontAwesomeIcon icon={faEarthEurope} />
          </a>
          <div>
            <span><i /> DJF WX-01</span>
            <strong>{selectedRun?.satellite || 'Satellite station'}</strong>
            <small>Updated {formatDate(selectedProduct?.mtime || selectedRun?.mtime)}</small>
          </div>
        </div>

        <section className="station-workbench three-panel">
          <aside className="station-channel-rail" aria-label="Image products">
            <div className="station-rail-head compact">
              <span>Products</span>
              <strong>{products.length}</strong>
            </div>
            <div className="station-channel-list">
              {products.map((product) => {
                const active = product.src === selectedProduct?.src;
                return (
                  <button
                    className={active ? 'active' : ''}
                    key={product.src}
                    type="button"
                    onClick={() => setSelectedProductSrc(product.src)}
                  >
                    <b aria-hidden="true">
                      <FontAwesomeIcon icon={productIcons[product.viewKey] || faImage} />
                    </b>
                    <span className="station-channel-flyout">
                      <strong>{product.label}</strong>
                      <small>{product.description}</small>
                    </span>
                  </button>
                );
              })}
              {!products.length && selectedRun && (
                <div className="station-product-empty">
                  <b>--</b>
                  <span>No image products</span>
                </div>
              )}
            </div>
          </aside>

          <div className="station-viewer-card">
            <div className="station-viewer-head">
              <div>
                <span>{runShortTitle(selectedRun)}</span>
                <strong>{selectedProduct?.label || selectedRun?.status?.replace(/-/g, ' ') || 'Station image'}</strong>
              </div>
              {selectedSrc && (
                <div className="station-viewer-controls">
                  <button type="button" onClick={() => adjustZoom(-0.25)}>-</button>
                  <span>{Math.round(view.scale * 100)}%</span>
                  <button type="button" onClick={() => adjustZoom(0.25)}>+</button>
                  <button type="button" onClick={() => setView({ scale: 1, x: 0, y: 0 })}>fit</button>
                  <a href={selectedSrc} target="_blank" rel="noreferrer">open</a>
                  <a
                    href={selectedSrc}
                    download={downloadFilename(selectedRun, selectedProduct)}
                    aria-label="Download selected satellite image"
                    title="Download selected image"
                  >
                    <FontAwesomeIcon icon={faDownload} /> download
                  </a>
                </div>
              )}
            </div>

            <div
              className="station-image-clip"
              onPointerDown={startDrag}
              onPointerMove={moveDrag}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              onWheel={handleWheel}
              role="presentation"
            >
              {products.length ? products.map((product) => {
                const active = product.src === selectedProduct?.src;
                const rotation = displayRotation(selectedRun, product);
                return (
                  <img
                    className={active ? 'active' : ''}
                    key={product.src}
                    src={stationAssetPath(product.src)}
                    alt={active ? `${runShortTitle(selectedRun)} ${product.label}` : ''}
                    aria-hidden={!active}
                    decoding="async"
                    draggable="false"
                    loading="eager"
                    style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale}) rotate(${rotation}deg)` }}
                  />
                );
              }) : selectedSrc ? (
                <img
                  className="active"
                  src={selectedSrc}
                  alt={`${runShortTitle(selectedRun)} ${selectedProduct?.label || ''}`}
                  decoding="async"
                  draggable="false"
                  style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale}) rotate(${imageRotation}deg)` }}
                />
              ) : selectedRun ? (
                <div className={`station-capture-result ${selectedRun.status || 'recorded'}`}>
                  <span>Capture result</span>
                  <strong>{selectedRun.status?.replace(/-/g, ' ') || 'recorded'}</strong>
                  <p>{selectedRun.subtitle}</p>
                  <small>{selectedRun.satellite} · {selectedRun.frequency} · {formatBytes(selectedRun.captureBytes)}</small>
                </div>
              ) : (
                <div className="station-empty">No station passes published yet.</div>
              )}
              {selectedSrc && (
                <div className="station-image-hud" aria-hidden="true">
                  <div className="station-hud-target">
                    <span>Orbital frame</span>
                    <strong>{selectedRun?.satellite || 'METEOR'}</strong>
                    <small>{selectedRun?.frequency || '137 MHz'} · {selectedRun?.status || 'decoded'}</small>
                  </div>
                  <div className="station-hud-product">
                    <span>{formatDate(selectedRun?.mtime)}</span>
                    <strong>{selectedProduct?.label || 'Satellite image'}</strong>
                  </div>
                  <div className="station-hud-instruction">Drag to pan · Scroll to zoom</div>
                </div>
              )}
            </div>
          </div>

          <aside className="station-run-rail" aria-label="Station passes">
            <div className="station-rail-head">
              <span>Passes</span>
              <strong>{runs.length}</strong>
            </div>
            <div className="station-thumb-list">
              {runs.map((run) => {
                const src = stationAssetPath(run.src);
                const active = (run.runId || run.title) === (selectedRun?.runId || selectedRun?.title);
                return (
                  <button
                    className={active ? 'active' : ''}
                    key={run.runId || run.title}
                    type="button"
                    onClick={() => selectRun(run)}
                  >
                    {src ? (
                      <img
                        src={src}
                        alt={runShortTitle(run)}
                        loading="lazy"
                        style={{ transform: `rotate(${displayRotation(run)}deg)` }}
                      />
                    ) : (
                      <div className={`station-run-placeholder ${run.status || 'recorded'}`}>
                        <b>{run.status === 'no-frame-lock' ? 'NO LOCK' : 'IQ'}</b>
                      </div>
                    )}
                    <span>{runShortTitle(run)}</span>
                    <small>{formatDate(run.mtime)} · {run.status?.replace(/-/g, ' ') || `${run.products?.length || 0} products`}</small>
                  </button>
                );
              })}
            </div>
          </aside>
        </section>

      </main>
    </div>
  );
}

export default StationPage;
